import { Link } from "@tanstack/react-router";
import { Compass, ExternalLink, MapPin, Phone, Star } from "lucide-react";
import type { BusinessDetail, ServiceSection, ServiceSectionItem } from "@/api/types";
import { Button } from "@/components/ui/button";
import { formatServiceDisplay } from "@/lib/service-display";
import { BusinessGallery } from "./business-gallery";
import { GalleryImage } from "./gallery-image";

interface BusinessPageProps {
  business: BusinessDetail;
}

// fallow-ignore-next-line complexity
function BusinessPage({ business }: BusinessPageProps) {
  const {
    name,
    slug,
    tagline,
    description,
    primary_category,
    rating,
    location,
    open_today,
    phone,
    website,
    gallery = [],
    service_sections = [],
    team = [],
    badges = [],
    hours = [],
    booking_enabled = true,
  } = business;

  const cover = gallery[0] ?? null;
  const categoryLabel = primary_category?.name;

  return (
    <>
      <div className="mx-auto max-w-[1100px] px-5 pb-20">
        {/* Cover */}
        {cover && (
          <div className="mt-8 block w-full overflow-hidden rounded-md bg-fog aspect-[16/6] min-h-[200px] max-h-[360px]">
            <GalleryImage key={cover} url={cover} alt={`${name} cover photo`} loading="eager" />
          </div>
        )}

        {/* Hero */}
        <section className="g-hero grid grid-cols-1 gap-6 py-8 md:grid-cols-[1.4fr_1fr] md:gap-9 md:items-start">
          <div>
            <div className="mb-3 flex items-center gap-4">
              {business.logo && (
                <GalleryImage
                  key={business.logo}
                  url={business.logo}
                  alt={`${name} logo`}
                  contain
                  compact
                  loading="eager"
                  className="size-16 shrink-0 rounded-md"
                />
              )}
              <h1 className="min-w-0 break-words font-display font-bold text-ink text-[clamp(32px,5vw,52px)] leading-[1.05] tracking-tight">
                {name}
              </h1>
            </div>
            {tagline && <p className="text-[16px] text-slate mb-[18px]">{tagline}</p>}

            {(categoryLabel || location?.city || rating) && (
              <div className="mb-4 flex flex-wrap items-center gap-2">
                {rating && (
                  <span className="inline-flex items-center gap-1 text-[14px] font-semibold text-ink">
                    <Star className="size-4 fill-[#F5A524] text-[#F5A524]" />
                    {rating.stars.toFixed(1)}
                    <span className="ml-0.5 font-normal text-slate">
                      ({rating.count.toLocaleString()})
                    </span>
                  </span>
                )}
                {categoryLabel && (
                  <span className="inline-flex h-8 items-center rounded-pill bg-fog px-3.5 text-[13px] font-medium text-ink">
                    {categoryLabel}
                  </span>
                )}
                {location?.city && (
                  <span className="inline-flex h-8 items-center gap-1 rounded-pill bg-fog px-3.5 text-[13px] font-medium text-ink">
                    <MapPin className="size-3.5 text-electric" />
                    {location.city}
                  </span>
                )}
                {open_today && (
                  <span className="text-[13px] text-slate">
                    · {open_today.replace(/^-\s*/, "")}
                  </span>
                )}
              </div>
            )}

            {description && (
              <div className="whitespace-pre-wrap text-ink text-[16px] leading-relaxed max-w-[70ch]">
                {description}
              </div>
            )}

            <div className="mt-4 flex flex-wrap gap-2.5">
              {phone && (
                <a
                  href={`tel:${phone.replace(/\s+/g, "")}`}
                  className="inline-flex h-9 items-center gap-1.5 rounded-md border border-fog bg-surface px-3.5 text-[14px] font-medium text-ink no-underline transition-colors duration-short ease-default hover:bg-fog"
                >
                  <Phone className="size-4 text-slate" />
                  {phone}
                </a>
              )}
              {website && (
                <a
                  href={website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-9 items-center gap-1.5 rounded-md border border-fog bg-surface px-3.5 text-[14px] font-medium text-ink no-underline transition-colors duration-short ease-default hover:bg-fog"
                >
                  <ExternalLink className="size-4 text-slate" />
                  Website
                </a>
              )}
              {location?.address_line_1 && (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    [location.address_line_1, location.city, location.postcode]
                      .filter(Boolean)
                      .join(", "),
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-9 items-center gap-1.5 rounded-md border border-fog bg-surface px-3.5 text-[14px] font-medium text-ink no-underline transition-colors duration-short ease-default hover:bg-fog"
                >
                  <Compass className="size-4 text-slate" />
                  Directions
                </a>
              )}
            </div>
          </div>

          {/* Map placeholder */}
          <div className="overflow-hidden rounded-md border border-fog bg-surface aspect-[4/3] min-h-[240px]">
            {location?.latitude != null && location?.longitude != null ? (
              <BusinessHeroMap lat={location.latitude} lng={location.longitude} />
            ) : (
              <div className="flex h-full items-center justify-center text-[14px] italic text-slate">
                {location ? "Map unavailable." : "No location on file yet."}
              </div>
            )}
          </div>
        </section>

        <BusinessGallery key={slug} name={name} photos={gallery} />

        {/* Services */}
        {service_sections.length > 0 && (
          <Section title="Services">
            <div className="flex flex-col gap-6">
              {service_sections.map((section) => (
                <ServiceSectionCard
                  key={section.section}
                  section={section}
                  slug={slug}
                  bookingEnabled={booking_enabled}
                />
              ))}
            </div>
          </Section>
        )}

        {/* Team */}
        {team.length > 0 && (
          <Section title="Team">
            <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-3.5">
              {team.map((member) => (
                <div
                  key={member.name}
                  className="rounded-md border border-fog bg-surface p-3.5 text-center"
                >
                  <div className="mx-auto mb-2.5 size-[88px] overflow-hidden rounded-full bg-fog">
                    {member.avatar_url && (
                      <img
                        src={member.avatar_url}
                        alt={member.name}
                        loading="lazy"
                        className="block size-full object-cover"
                      />
                    )}
                  </div>
                  <div className="text-[14px] font-semibold text-ink">{member.name}</div>
                  {member.role && (
                    <div className="mt-0.5 text-[12px] text-slate">{member.role}</div>
                  )}
                  {member.rating != null && (
                    <div className="mt-1 inline-flex items-center gap-0.5 text-[12px]">
                      <Star className="size-3 fill-[#F5A524] text-[#F5A524]" />
                      <span className="text-[#F5A524]">{member.rating}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Hours + Badges */}
        {(hours.length > 0 || badges.length > 0) && (
          <Section>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {hours.length > 0 && (
                <div className="rounded-md border border-fog bg-surface p-5">
                  <h3 className="mb-2.5 text-[12px] font-bold uppercase tracking-[0.06em] text-slate">
                    Opening hours
                  </h3>
                  {hours.map((h) => (
                    <div
                      key={h.day}
                      className={`flex items-center justify-between border-t border-fog py-1.5 text-[14px] first:border-t-0 ${h.today ? "font-semibold" : ""}`}
                    >
                      <span className="text-ink">{h.day}</span>
                      <span className={h.closed ? "italic text-slate" : "text-slate"}>
                        {h.range || (h.closed ? "Closed" : "—")}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {badges.length > 0 && (
                <div className="rounded-md border border-fog bg-surface p-5">
                  <h3 className="mb-2.5 text-[12px] font-bold uppercase tracking-[0.06em] text-slate">
                    Features
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {badges.map((b) => (
                      <span
                        key={b.label}
                        className="inline-flex items-center gap-1.5 rounded-pill border border-fog bg-surface px-3 py-1.5 text-[13px] text-ink"
                      >
                        {b.icon_url && <img src={b.icon_url} alt="" className="size-4" />}
                        {b.label}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Section>
        )}

        {/* Location info */}
        <Section title="Location">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="rounded-md border border-fog bg-surface p-5">
              {location ? (
                <dl>
                  <dt className="mb-1.5 text-[12px] uppercase tracking-[0.06em] text-slate">
                    Address
                  </dt>
                  <dd className="mb-3.5 text-[15px] leading-relaxed text-ink">
                    {[location.address_line_1, location.city, location.postcode]
                      .filter(Boolean)
                      .join(", ") || "—"}
                  </dd>
                  {location.timezone && (
                    <>
                      <dt className="mb-1.5 text-[12px] uppercase tracking-[0.06em] text-slate">
                        Timezone
                      </dt>
                      <dd className="text-[15px] text-ink">{location.timezone}</dd>
                    </>
                  )}
                </dl>
              ) : (
                <p className="text-[14px] italic text-slate">No location on file yet.</p>
              )}
            </div>
            <div className="rounded-md border border-fog bg-surface p-5">
              <dl>
                {phone && (
                  <>
                    <dt className="mb-1.5 text-[12px] uppercase tracking-[0.06em] text-slate">
                      Phone
                    </dt>
                    <dd className="mb-3.5">
                      <a
                        href={`tel:${phone.replace(/\s+/g, "")}`}
                        className="text-[15px] text-ink underline underline-offset-[3px]"
                      >
                        {phone}
                      </a>
                    </dd>
                  </>
                )}
                {website && (
                  <>
                    <dt className="mb-1.5 text-[12px] uppercase tracking-[0.06em] text-slate">
                      Website
                    </dt>
                    <dd className="mb-3.5">
                      <a
                        href={website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[15px] text-ink underline underline-offset-[3px]"
                      >
                        {website}
                      </a>
                    </dd>
                  </>
                )}
                <dt className="mb-1.5 text-[12px] uppercase tracking-[0.06em] text-slate">
                  Currency
                </dt>
                <dd className="text-[15px] text-ink">{business.currency ?? "—"}</dd>
              </dl>
            </div>
          </div>
        </Section>
      </div>

      {/* Sticky book CTA */}
      <div className="sticky bottom-0 z-30 border-t border-fog bg-white/95 backdrop-blur-[8px] px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] shadow-pop">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4">
          <span className="truncate text-[15px] font-medium text-ink">{name}</span>
          {booking_enabled ? (
            <Button asChild variant="primary" size="lg" className="shrink-0">
              <Link to="/businesses/$slug/book" params={{ slug }}>
                Book Now
              </Link>
            </Button>
          ) : (
            <div className="flex shrink-0 flex-col items-end gap-1 text-right">
              <span className="text-[13px] text-slate">By appointment only — call us to book.</span>
              {phone && (
                <a
                  href={`tel:${phone.replace(/\s+/g, "")}`}
                  className="text-[15px] font-semibold text-electric-800 no-underline hover:underline"
                >
                  {phone}
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function Section({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <section className="border-t border-fog py-8">
      {title && (
        <h2 className="font-display font-semibold text-[24px] tracking-tight mb-4">{title}</h2>
      )}
      {children}
    </section>
  );
}

const serviceRowBaseClass =
  "grid grid-cols-[1fr_auto] gap-1.5 rounded-xl border border-fog bg-surface p-4 text-left";

const serviceRowInteractiveClass = `${serviceRowBaseClass} no-underline transition-[border-color,box-shadow] duration-short ease-default hover:border-electric hover:shadow-[0_0_0_1px_var(--electric)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-electric focus-visible:ring-offset-2`;

function ServiceSectionCard({
  section,
  slug,
  bookingEnabled,
}: {
  section: ServiceSection;
  slug: string;
  bookingEnabled: boolean;
}) {
  return (
    <div>
      <h3 className="mb-2.5 text-[12px] font-bold uppercase tracking-[0.06em] text-slate">
        {section.section}
      </h3>
      <div className="flex flex-col gap-2">
        {section.items.map((item) => (
          <ServiceRow
            key={item.id ?? item.name}
            item={item}
            slug={slug}
            bookingEnabled={bookingEnabled}
          />
        ))}
      </div>
    </div>
  );
}

function ServiceRow({
  item,
  slug,
  bookingEnabled,
}: {
  item: ServiceSectionItem;
  slug: string;
  bookingEnabled: boolean;
}) {
  const { duration, price } = formatServiceDisplay(item);
  const content = (
    <>
      <div>
        <div className="text-[15px] font-semibold text-ink">{item.name}</div>
        {duration && <div className="text-[13px] text-slate">{duration}</div>}
      </div>
      <div className="text-right text-[15px] font-semibold text-ink">{price ?? "—"}</div>
      {item.description && (
        <div className="col-span-full text-[13px] leading-relaxed text-slate">
          {item.description}
        </div>
      )}
    </>
  );

  if (!bookingEnabled || !item.id) {
    return <div className={serviceRowBaseClass}>{content}</div>;
  }

  return (
    <Link
      to="/businesses/$slug/book"
      params={{ slug }}
      search={{
        step: "schedule",
        service_id: item.id,
        expected_service_terms_version: item.booking_terms_version,
        service_name: item.name,
      }}
      className={serviceRowInteractiveClass}
    >
      {content}
    </Link>
  );
}

function BusinessHeroMap({ lat, lng }: { lat: number; lng: number }) {
  return (
    <iframe
      title="Location map"
      className="block size-full min-h-[240px] border-0"
      src={`https://www.openstreetmap.org/export/embed.html?bbox=${lng - 0.005},${lat - 0.003},${lng + 0.005},${lat + 0.003}&layer=mapnik&marker=${lat},${lng}`}
      loading="lazy"
    />
  );
}

export { BusinessPage, type BusinessPageProps };
